/**
 * PythonFileSystemHelper - Helper for managing Pyodide filesystem operations
 *
 * This helper handles all filesystem-related operations for Python code execution,
 * including mounting files, unmounting, diagnostics, and project file management.
 */

import { GeneralHelpers } from '../../../helpers/general.helper';
import { PythonUtils } from '../../../helpers/python-utils.helper';
import { BatchUpsertError, BatchUpsertResponse, NotebookFile } from '@app/shared/interfaces/file.interface';
import { PyodideFileJournal } from '../journal/python-journal.helper';
import { ModuleListHelper } from '../../../helpers/module-list.helper';
import {
  FileSystemConfig,
  FileSystemDiagnostics,
  AddFilesResult,
  UnmountResult
} from '../interfaces/python-filesystem.interface';
import { PYTHON_DIRECTORIES, PYFS_CACHE_KEYS } from '../interfaces/python.interface';
import { FILE_EXTENSIONS, RELOAD_NOTEBOOK_FILES } from '../../../constants/general.constants';
import { PythonHelper } from '../helpers/python.helper';
import { FileService } from '../../file/file.service';
import { NotebookFilesService } from '../../notebook-files/notebook-files.service';
import { NotebookDefaultFilesService } from '../../notebook-default-file/notebook-default-file.service';
import { CacheService } from '../../cache/cache.service';
import { FileDataService } from '../../file-data/file-data.service';
import { AppStateService } from '../../app-state/app-state.service';
import { MessageService } from '../../message/message.service';
import { firstValueFrom } from 'rxjs';
import { FileSystemProxy } from './python-filesystem-proxy.helper';
import { LocalforageService } from '../../localforage/localforage.service';
import { ExecutionContext } from '@app/shared/interfaces/chunk/chunk-context.interface';
import {
  batchResponseBody,
  chunkSyncUploads,
  isBatchEndpointAbsent,
  MAX_BATCH_BYTES,
} from './python-filesystem-batch.helper';

/** File content accepted by the Pyodide virtual-filesystem write path. */
type FileContent = ArrayBuffer | Uint8Array | Blob | string;

/**
 * A `NotebookFile` as it flows through the Python filesystem layer: the shared
 * interface plus the loosely-attached routing/content fields that the module
 * list and content-prefetch paths tack on at runtime. Declared locally rather
 * than widening the shared `NotebookFile` interface, which is declared in two
 * places and consumed repo-wide.
 */
type PyFsFile = NotebookFile & {
  content?: FileContent;
  source?: string;
  isGlobal?: boolean;
};

/**
 * The loosely-assembled service bag threaded into the add/fetch file paths.
 * Members are optional because callers wire up whichever services a given
 * operation needs; `fileUnifiedService` is described structurally to avoid a
 * hard dependency cycle on its concrete class.
 */
interface PyFsFileServices {
  fileService?: FileService;
  fileDataService?: FileDataService;
  fileUnifiedService?: {
    getFile(args: unknown[], context?: unknown, source?: string): Promise<FileContent>;
  };
}

/**
 * Minimal shape of a backend file record consulted while syncing the Pyodide FS
 * back to cache/default-file APIs. The concrete records differ between APIs, so
 * only the fields the sync path actually reads are described here.
 */
interface SyncFileRecord {
  id?: number | string;
  fileId?: number | string;
  name?: string;
  content?: ArrayBuffer | null;
}

export class PythonFileSystemHelper {
  // `python` (the Pyodide runtime) and `micropip` are dynamic PyProxy-backed
  // handles whose surface Pyodide's own types only loosely describe; kept as
  // `any` per the audit (partly inherent) and ADR-0005 (Pyodide typing is
  // revisited with the Worker-isolation work, not this pass).
  private readonly python: any;
  private readonly micropip: any;
  private config: FileSystemConfig;
  private readonly journal?: PyodideFileJournal;
  private chunkContext?: ExecutionContext;
  private readonly fsProxy?: FileSystemProxy; // For trusted operations
  private readonly localforageService?: LocalforageService; // For hash-based optimization

  constructor(
    python: any,
    micropip: any,
    config: FileSystemConfig,
    journal?: PyodideFileJournal,
    chunkContext?: ExecutionContext,
    fsProxy?: FileSystemProxy,  // Optional for backward compatibility
    localforageService?: LocalforageService  // Optional for hash-based write optimization
  ) {
    this.python = python;
    this.micropip = micropip
    this.config = config;
    this.journal = journal;
    this.chunkContext = chunkContext;
    this.fsProxy = fsProxy;
    this.localforageService = localforageService;
  }

  /**
   * SIMPLIFIED: Universal method to add a file with metadata tracking
   * This is the main entry point for all file operations
   * Updated: Supports subdirectory routing for proper file placement
   */
  async addFile(
    fileName: string,
    content: ArrayBuffer | Uint8Array | Blob | string,
    metadata: {
      origin: 'user' | 'project' | 'generated' | 'cache';
      purpose?: 'module' | 'data' | 'output' | 'temp';
      persistent?: boolean;
    },
    file?: NotebookFile & { source?: string; isGlobal?: boolean }  // Optional: for subdirectory routing
  ): Promise<{ success: boolean; path: string; errors?: string[] }> {
    const filePath = this.buildFilePath(fileName, file);

    return this.executeFileOperation(async () => {
      // Step 1: Write to FS
      const size = await this.writeFileToFS(filePath, content);

      // Step 2: Journal with metadata
      if (this.journal) {
        const fileType = this.extractFileType(fileName);
        this.journal.logFileCreation(filePath, metadata.origin, {
          origin: metadata.origin,
          fileType,
          timestamp: Date.now(),
          persistent: metadata.persistent,
          purpose: metadata.purpose,
          size
        });
      }

      // Step 3: Auto-process Python files
      await this.processFileByExtension(fileName, filePath);
    }, fileName, filePath, 'Failed to add file');
  }

  /**
   * SIMPLIFIED: Register Python file by adding to sys.path
   */
  private async registerPythonFile(_filePath: string): Promise<void> {
    try {
      // Simply ensure current directory is in sys.path
      this.python.runPython(`
import sys
if '.' not in sys.path:
    sys.path.insert(0, '.')
      `);
    } catch (error) {
      console.warn('Python filesystem: failed to update sys.path', error);
    }
  }

  /**
   * Determine target subdirectory based on file metadata and UI sidebar source
   * Uses existing metadata fields - NO new fields added
   *
   * UI Architecture:
   * - LEFT SIDEBAR: "Project Files" → ./project/ (except .whl)
   * - RIGHT SIDEBAR: "Workflow Files" → ./default/ or ./files/ (by label)
   *
   * Routing rules:
   * 1. .whl files → ROOT (for micropip installation, then deleted)
   * 2. Project Files (versionId exists OR source='moduleList') → ./project/
   * 3. Workflow Files with label "default" (isDefaultFile=true) → ./default/
   * 4. Workflow Files with label "cache" → ./files/
   *
   * File identification:
   * - Project Files have: versionId field (from LEFT sidebar)
   * - Module List files have: source='moduleList'
   * - Workflow Default files have: isDefaultFile=true
   * - Workflow Cache files have: cacheId field (no versionId, no isDefaultFile)
   *
   * This ensures:
   * - os.listdir("project") shows ALL Project Files (not just .py)
   * - os.listdir("default") shows workflow default files
   * - os.listdir("files") shows workflow cache files
   * - os.listdir("./") shows only temporary root files
   *
   * @param file - File with metadata (name, versionId, isDefaultFile, source)
   * @returns Subdirectory name ('', 'files', 'default', 'project')
   */
  private getFileSubdirectory(file: NotebookFile & { source?: string; isGlobal?: boolean }, fileName?: string): string {
    return PythonHelper.resolveFileSubdirectory(file, fileName);
  }

  /**
   * Base method for loading files to subdirectories
   * Shared logic between loadModuleOnly and loadDataFileOnly
   * Reduces code duplication (Sonar fix)
   *
   * @param fileName - Name of the file to load
   * @param content - File content (string, ArrayBuffer, Uint8Array, Blob)
   * @param fileType - Type of file ('module' for .py, 'data' for others)
   * @param file - Optional file metadata for subdirectory routing
   * @returns Result with success status and file path
   */
  private async loadFileToSubdirectory(
    fileName: string,
    content: FileContent,
    fileType: 'module' | 'data',
    file?: NotebookFile & { source?: string }
  ): Promise<{ success: boolean; path: string; errors?: string[] }> {
    const subdirectory = file ? this.getFileSubdirectory(file) : PYTHON_DIRECTORIES.FILES_SUBDIR;
    const filePath = this.buildFilePath(fileName, file, subdirectory);

    return this.executeFileOperation(async () => {
      // Write to FS (FileSystemProxy will automatically journal)
      await this.writeFileToFS(filePath, content);

      // Module-specific: register .py files in sys.path
      if (fileType === 'module') {
        await this.processFileByExtension(fileName, filePath);
      }
    }, fileName, filePath, `Failed to load ${fileType === 'module' ? 'module' : 'data file'}`);
  }

  /**
   * SIMPLIFIED: Install wheel package via micropip with validation
   * PHASE 4: Added duplicate check and validation to prevent BadZipFile errors
   * Updated to use emfs: protocol for better Pyodide compatibility
   */
  async installWheelPackage(filePath: string): Promise<void> {
    try {
      // STEP 1: Validate file exists
      if (!this.python.FS.analyzePath(filePath).exists) {
        return;
      }

      // STEP 2: Check if package already installed to avoid duplicate installation
      const packageName = this.extractPackageNameFromWheelPath(filePath);
      if (packageName && await this.isPackageAlreadyInstalled(packageName)) {
        return;
      }

      // STEP 3: Validate that file is a valid zip (wheel is zip format)
      const isValidZip = await this.validateWheelFile(filePath);
      if (!isValidZip) {
        return;
      }

      // STEP 4: Install via micropip using emfs: protocol for Pyodide MEMFS
      // Use emfs: protocol which is optimized for Pyodide's virtual filesystem
      const emfsPath = `emfs:${filePath}`;
      await this.micropip.install(emfsPath);

    } catch (error) {
      console.warn('Python filesystem: wheel install failed', { filePath, error });
    }
  }

  /**
   * Extract package name from wheel file path
   */
  private extractPackageNameFromWheelPath(filePath: string): string {
    try {
      const fileName = filePath.split('/').pop() || '';
      // Wheel format: package_name-version-python_tag-abi_tag-platform_tag.whl
      // Remove .whl extension first
      const nameWithoutExt = fileName.replace(/\.whl$/, '');
      // Split by dash and find where the version starts (first numeric part)
      const parts = nameWithoutExt.split('-');

      // Find the index where version starts (first part that starts with a digit)
      let versionIndex = parts.findIndex(part => /^\d/.test(part));
      if (versionIndex === -1) {
        // No version found, take the first part
        versionIndex = 1;
      }

      // Package name is everything before the version
      const namePart = parts.slice(0, versionIndex).join('-');
      // Normalize: replace hyphens with underscores and lowercase
      return namePart.replace(/-/g, '_').toLowerCase();
    } catch (error) {
      console.warn('Python filesystem: failed to parse wheel name', { filePath, error });
      return '';
    }
  }

  /**
   * Check if package is already installed in Python environment
   */
  private async isPackageAlreadyInstalled(packageName: string): Promise<boolean> {
    try {
      const pythonCode = `
import importlib.util
import sys
import json

# Try multiple package name variations
variations = ['${packageName}', '${packageName}'.replace('_', '-'), '${packageName}'.replace('-', '_')]

result = False
for variation in variations:
    try:
        # Check if package is in installed packages
        import pkg_resources
        installed = [p.project_name.lower() for p in pkg_resources.working_set]
        if variation.lower() in installed:
            print(f"📦 Found {variation} in installed packages")
            result = True
            break
    except:
        pass

    try:
        # Check if module can be imported
        spec = importlib.util.find_spec(variation)
        if spec is not None:
            print(f"📦 Found {variation} as importable module")
            result = True
            break
    except:
        pass

json.dumps(result)
      `;
      return this.executePythonCode<boolean>(pythonCode) === true;
    } catch (error) {
      console.warn('Python filesystem: package check failed, assuming not installed', error);
      return false;
    }
  }

  /**
   * Validate that wheel file is a valid zip file
   */
  private async validateWheelFile(filePath: string): Promise<boolean> {
    try {
      const pythonCode = `
import zipfile
import os
import json

try:
    # Check if file exists and is readable
    if not os.path.exists('${filePath}') or not os.path.isfile('${filePath}'):
        print(f"File does not exist or is not a file: ${filePath}")
        result = False
    else:
        # Try to open as zip file
        with zipfile.ZipFile('${filePath}', 'r') as zf:
            # Validate that it has wheel metadata
            files = zf.namelist()
            has_metadata = any(f.endswith('.dist-info/METADATA') for f in files)
            print(f"Wheel file validation: has metadata = {has_metadata}")
            result = has_metadata
except zipfile.BadZipFile as e:
    print(f"Invalid zip file: {e}")
    result = False
except Exception as e:
    print(f"Validation error: {e}")
    result = False

json.dumps(result)
      `;
      return this.executePythonCode<boolean>(pythonCode) === true;
    } catch (error) {
      console.warn('Python filesystem: wheel validation failed', { filePath, error });
      return false;
    }
  }

  /**
   * Helper to extract file type from name
   */
  private extractFileType(fileName: string): string {
    const parts = fileName.split('.');
    // Return lowercase to match GeneralHelpers.fileExtensionFromString() behavior
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
  }

  /**
   * Capitalize first letter of a string
   * Reduces code duplication for file type labels
   */
  private capitalizeFirst(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  /**
   * Calculate size of content in bytes
   * Reduces code duplication for content size calculation
   */
  private calculateContentSize(content: unknown): number {
    if (typeof content === 'string') {
      return content.length;
    } else if (content instanceof ArrayBuffer) {
      return content.byteLength;
    } else if (content instanceof Uint8Array) {
      return content.byteLength;
    } else if (content && typeof content === 'object' && 'byteLength' in content) {
      return Number((content as { byteLength: unknown }).byteLength) || 0;
    }
    return 0;
  }

  /**
   * Format hash for display (first 8 characters)
   * Reduces code duplication for hash display
   */
  private formatHash(hash: string): string {
    return hash.substring(0, 8);
  }

  /**
   * Check if error is a duplicate file error
   * Reduces code duplication for error checking
   */
  private isDuplicateFileError(error: unknown): boolean {
    const err = error as { body?: unknown; message?: unknown; status?: unknown } | null | undefined;
    const errorMsg = String(err?.body ?? err?.message ?? error);
    return errorMsg.includes('duplicate') ||
      errorMsg.includes('unique constraint') ||
      err?.status === 400;
  }

  /**
   * Get response type for file based on extension
   * Reduces code duplication for response type determination
   */
  private getFileResponseType(fileName: string, fileDataService: FileDataService): 'text' | 'blob' | 'arraybuffer' {
    const fileExtension = GeneralHelpers.fileExtensionFromString(fileName);
    return fileDataService.getResponseType(fileExtension) as 'text' | 'blob' | 'arraybuffer';
  }

  /**
   * Notify UI to reload file list after file sync operations
   * Sends RELOAD_NOTEBOOK_FILES message if any files were processed
   * This ensures UI updates even when all files are skipped due to filtering
   */
  private notifyUIAfterFileSync(
    stats: { filesCreated: number; filesUpdated: number; filesSkipped: number; filesFailed?: number },
    messageService: MessageService
  ): void {
    const failed = stats.filesFailed ?? 0;
    if (stats.filesCreated > 0 || stats.filesUpdated > 0 || stats.filesSkipped > 0 || failed > 0) {
      messageService.sendMessage(RELOAD_NOTEBOOK_FILES);
    }
    if (failed > 0) {
      // A failed file is one that did NOT reach the backend: an ambiguous
      // basename, content we refused to read, or a batch item whose sequential
      // retry also failed. Console-only left the person whose work did not
      // persist with no sign of it.
      messageService.showMessage(
        'error',
        `${failed} file${failed === 1 ? '' : 's'} could not be saved to the project — see the browser console for which and why`
      );
    }
  }

  /**
   * Process module list items and convert to NotebookFile format
   * Reduces code duplication for .py and .whl module processing
   */
  private async processModuleListItems(
    modules: Array<{ fileId: number; name: string; versionId?: number | string; isGlobal?: boolean }>,
    fileService: FileService,
    fileType: 'python' | 'wheel',
    responseType: 'text' | 'arraybuffer'
  ): Promise<NotebookFile[]> {
    const convertedModules: PyFsFile[] = [];
    const _fileExtension = fileType === 'python' ? '.py' : '.whl';

    for (const module of modules) {
      try {
        const content = await firstValueFrom(fileService.getById(module.fileId, responseType));

        if (content) {
          const filesize = typeof content === 'string' ? content.length : content.byteLength;
          convertedModules.push({
            name: module.name,
            fileId: module.fileId,
            cacheId: module.fileId,
            filesize,
            modifiedAt: new Date().toISOString(),
            versionId: String(module.versionId || ''),
            fileType,
            content: content,
            isGlobal: module.isGlobal || false,
            source: 'moduleList'
          } as PyFsFile);
        }
      } catch (error) {
        console.warn('Python filesystem: failed to load module content', { module, error });
      }
    }

    return convertedModules;
  }

  /**
   * Load file with trusted operation wrapper
   * Reduces code duplication between loadModuleOnly and loadDataFileOnly
   */
  private async loadFileWithTrustedOperation(
    fileName: string,
    content: FileContent,
    fileType: 'module' | 'data',
    file?: NotebookFile & { source?: string },
    errorMessage?: string
  ): Promise<{ success: boolean; path: string; errors?: string[] }> {
    try {
      // PROT-810: Wrap in trusted operation for system loading
      if (this.fsProxy) {
        return await this.fsProxy.executeTrustedOperation(() => {
          return this.loadFileToSubdirectory(fileName, content, fileType, file);
        });
      }

      // Fallback: direct call (backward compatibility if no proxy)
      return await this.loadFileToSubdirectory(fileName, content, fileType, file);
    } catch (error) {
      const errors = [errorMessage || `Failed to load ${fileType} ${fileName}: ${GeneralHelpers.errorMessage(error)}`];
      return {
        success: false,
        path: `${this.config.workingDir}/files/${fileName}`,
        errors
      };
    }
  }

  /**
   * Build file path with subdirectory routing
   * Reduces code duplication (Sonar fix)
   *
   * @param fileName - Name of the file
   * @param file - Optional file metadata for subdirectory routing
   * @param defaultSubdir - Default subdirectory if file metadata not provided
   * @returns Full file path
   */
  private buildFilePath(
    fileName: string,
    file?: NotebookFile & { source?: string; isGlobal?: boolean },
    defaultSubdir: string = ''
  ): string {
    const fileForRouting = file ? { ...file, name: file.name || fileName } : undefined;
    const subdirectory = fileForRouting ? this.getFileSubdirectory(fileForRouting, fileName) : defaultSubdir;
    return subdirectory
      ? `${this.config.workingDir}/${subdirectory}/${fileName}`
      : `${this.config.workingDir}/${fileName}`;
  }

  /**
   * Execute Python code and parse JSON result
   * Reduces code duplication (Sonar fix)
   *
   * @param pythonCode - Python code to execute (should return JSON)
   * @returns Parsed JSON result
   */
  private executePythonCode<T = unknown>(pythonCode: string): T {
    const result = this.python.runPython(pythonCode);
    // Convert Python string to JavaScript string before parsing JSON
    const jsResult = typeof result === 'string' ? result : (result?.toJs ? result.toJs() : String(result));
    return JSON.parse(jsResult);
  }

  /**
   * Process file based on extension (register Python files, install wheels)
   * Reduces code duplication (Sonar fix)
   *
   * @param fileName - Name of the file
   * @param filePath - Full path to the file
   */
  private async processFileByExtension(fileName: string, filePath: string): Promise<void> {
    const fileExtension = GeneralHelpers.fileExtensionFromString(fileName);
    if (fileExtension === FILE_EXTENSIONS.py) {
      await this.registerPythonFile(filePath);
    } else if (fileExtension === FILE_EXTENSIONS.whl) {
      await this.installWheelPackage(filePath);
    }
  }

  /**
   * Execute file operation with standardized error handling
   * Reduces code duplication (Sonar fix)
   *
   * @param operation - Async operation to execute
   * @param fileName - Name of the file being processed
   * @param filePath - Full path to the file
   * @param errorPrefix - Prefix for error messages (e.g., "Failed to add file")
   * @returns Result with success status, path, and errors
   */
  private async executeFileOperation<T = void>(
    operation: () => Promise<T>,
    fileName: string,
    filePath: string,
    errorPrefix: string
  ): Promise<{ success: boolean; path: string; errors?: string[]; result?: T }> {
    const errors: string[] = [];
    try {
      const result = await operation();
      return { success: true, path: filePath, result };
    } catch (error) {
      errors.push(`${errorPrefix} ${fileName}: ${GeneralHelpers.errorMessage(error)}`);
      return { success: false, path: filePath, errors };
    }
  }

  /**
   * Add files to Pyodide filesystem (legacy method - now uses simplified addFile)
   */
  async addFilesToPyodide(files: NotebookFile[], services?: PyFsFileServices): Promise<AddFilesResult> {
    const _startTime = performance.now();
    const result: AddFilesResult = {
      success: true,
      addedFiles: [],
      skippedFiles: [],
      errors: [],
      totalSize: 0
    };


    // Clean up missing files using journal metadata (Phase 2: replaced tracker)
    if (this.journal) {
      try {
        await this.unmountMissingFilesUsingJournal(files);
      } catch (error) {
        console.warn('Python filesystem: journal cleanup failed', error);
      }
    }

    // Check working directory before adding files
    this.python.runPython('import os; os.getcwd()');

    for (const file of files) {
      try {
        const fileResult = await this.addSingleFile(file, services);
        if (fileResult.success) {
          result.addedFiles.push(file.name);
          result.totalSize += fileResult.size;
        } else {
          result.skippedFiles.push(file.name);
          result.errors.push(...fileResult.errors);
        }
      } catch (error) {
        result.errors.push(`Error processing file ${file.name}: ${GeneralHelpers.errorMessage(error)}`);
        result.skippedFiles.push(file.name);
      }
    }

    // Final verification
    const finalCheck = await this.verifyFilesAccessibility(result.addedFiles);
    if (!finalCheck.success) {
      result.errors.push(...finalCheck.errors);
      result.success = false;
    }


    return result;
  }

  /**
   * Add a single file to Pyodide filesystem
   * Updated: Uses subdirectory routing to place files in correct folders
   */
  private async addSingleFile(file: NotebookFile, services?: PyFsFileServices): Promise<{ success: boolean, size: number, errors: string[] }> {
    const fileName = file.name;
    const filePath = this.buildFilePath(fileName, file);
    const errors: string[] = [];

    // Guard: skip if already exists
    if (this.fileAlreadyExists(filePath)) {
      this.logInfo(`📄 File ${fileName} already exists, skipping`);
      return { success: false, size: 0, errors: [`File ${fileName} already exists`] };
    }

    // Resolve content
    const content = await this.resolveFileContent(file, services);
    if (!content) {
      errors.push(`No content available for file ${fileName}`);
      return { success: false, size: 0, errors };
    }

    try {
      // Write file based on content type
      const size = await this.writeFileToFS(filePath, content);

      // Journal the file operation
      this.journalCreation(filePath);

      // Verify file accessibility (non-fatal)
      await this.verifyAndCollect(fileName, errors);

      // Auto-add to module list if applicable (non-fatal)
      await this.safeAddToModuleList(fileName, file, services);

      this.logInfo(`📁 Successfully added file ${fileName} (${size} bytes)`);
      return { success: true, size, errors };
    } catch (error) {
      errors.push(`Failed to add file ${fileName}: ${GeneralHelpers.errorMessage(error)}`);
      return { success: false, size: 0, errors };
    }
  }

  // =============== Extracted helpers to reduce cognitive complexity ===============
  private fileAlreadyExists(filePath: string): boolean {
    try {
      return this.python.FS.analyzePath(filePath).exists;
    } catch {
      return false;
    }
  }

  private async resolveFileContent(file: NotebookFile, services?: PyFsFileServices): Promise<FileContent | undefined> {
    const direct = (file as PyFsFile).content;
    if (direct) return direct;
    if (file.name && services) {
      return this.fetchFileContent(file, services);
    }
    return undefined;
  }

  private journalCreation(filePath: string): void {
    if (this.journal) {
      this.journal.logFileCreation(filePath, 'user-file');
    }
  }

  private async verifyAndCollect(fileName: string, errors: string[]): Promise<void> {
    const accessible = await this.verifyFileAccess(fileName);
    if (!accessible) {
      errors.push(`File ${fileName} was written but not accessible from Python`);
    }
  }

  // REMOVED: safeAutoRegister - functionality moved to simplified addFile() method

  private logInfo(_message: string): void {
    // Logging disabled
  }

  private async safeAddToModuleList(fileName: string, file: NotebookFile, services?: PyFsFileServices): Promise<void> {
    if (!services?.fileService) return;

    // ✅ CRITICAL FIX: Skip files from Module List - they are already in the list!
    // Auto-add should ONLY happen for newly uploaded files, NOT for files loaded from Module List
    // This prevents duplicates when reloading project
    const isFromModuleList = (file as PyFsFile).source === 'moduleList' || file.versionId;
    if (isFromModuleList) {
      return;  // Skip - already in Module List
    }

    try {
      // Delegate to centralized ModuleListHelper. The module-list store persists
      // this content to LocalForage typed as `ArrayBuffer | string`, so normalize
      // the (possibly Uint8Array/Blob) PyFsFile content to an ArrayBuffer first.
      const raw = (file as PyFsFile).content;
      let content: ArrayBuffer | string | undefined;
      if (raw instanceof Uint8Array) {
        // Copy into a standalone ArrayBuffer (the view's backing buffer may be a
        // SharedArrayBuffer and may be larger than the view).
        const copy = new ArrayBuffer(raw.byteLength);
        new Uint8Array(copy).set(raw);
        content = copy;
      } else if (raw instanceof Blob) {
        content = await raw.arrayBuffer();
      } else {
        content = raw;
      }
      await ModuleListHelper.autoAddFileToModulesIfEligible(file, services.fileService, content);
    } catch (error) {
      console.warn('Python filesystem: failed to add module to list', { fileName: file.name, error });
    }
  }

  /**
   * Ensure parent directory exists before file write
   * Prevents ErrnoError errno: 2 (ENOENT - No such file or directory)
   *
   * @param filePath - Full path to file (e.g., /home/pyodide/project/file.py)
   */
  // private ensureParentDirectoryExists(filePath: string): void {
  //   const lastSlash = filePath.lastIndexOf('/');
  //   if (lastSlash === -1) {
  //     return; // No directory component in path
  //   }

  //   const parentDir = filePath.substring(0, lastSlash);

  //   // Check if parent directory exists
  //   try {
  //     const pathInfo = this.python.FS.analyzePath(parentDir);
  //     if (!pathInfo.exists) {
  //       console.log(`📁 Creating missing parent directory: ${parentDir}`);
  //       this.python.FS.mkdirTree(parentDir);
  //       console.log(`✅ Directory created: ${parentDir}`);
  //     }
  //   } catch (error: any) {
  //     console.warn(`⚠️ Could not verify/create directory ${parentDir}:`, error);
  //     // Try to create anyway
  //     try {
  //       this.python.FS.mkdirTree(parentDir);
  //     } catch (createError) {
  //       // Already exists or creation failed - will fail on writeFile if real problem
  //     }
  //   }
  // }

  /**
   * Write content to filesystem based on its type
   * OPTIMIZATION: Hash-based skip using Journal + LocalforageService
   * CRITICAL FIX: Ensures parent directory exists before writing (prevents errno 2)
   */
  private async writeFileToFS(filePath: string, content: unknown): Promise<number> {
    // STEP 1: Calculate hash of incoming content
    const newHash = await GeneralHelpers.calculateFileHash(content);

    // STEP 2: Check Journal (in-memory, fastest)
    if (this.journal) {
      const journalMeta = this.journal.getFileMetadataByPath(filePath);

      if (journalMeta?.hash === newHash) {
        return journalMeta.size || 0;
      }
    }

    // STEP 3: Check LocalforageService (persistent, medium speed)
    if (this.localforageService) {
      const cacheKey = `${PYFS_CACHE_KEYS.META_PREFIX}${filePath}`;

      try {
        const cached = await this.localforageService.getItem(cacheKey) as { hash?: string; size?: number; timestamp?: number } | null;

        if (cached && cached.hash === newHash) {
          // CRITICAL: Check if file actually exists in Pyodide FS before skipping
          // LocalforageService persists across page reloads, but Pyodide FS is volatile
          try {
            const fsExists = this.python.FS.analyzePath(filePath).exists;

            if (fsExists) {
              // File exists with matching hash - safe to skip
              // Update Journal with cached metadata (restore from persistent cache)
              if (this.journal) {
                this.journal.updateFileMetadata(filePath, {
                  hash: cached.hash,
                  size: cached.size,
                  timestamp: cached.timestamp
                });
              }

              return cached.size ?? 0;
            }
            // Fall through to write
          } catch (error) {
            console.debug('Python filesystem: FS existence check failed', { filePath, error });
          }
        }
      } catch (error) {
        console.debug('Python filesystem: cache lookup failed', { cacheKey, error });
      }
    }

    // STEP 4: Hash mismatch or cache miss - write to FS
    let size: number;

    const _parentDir = filePath.substring(0, filePath.lastIndexOf('/'));

    // Write content based on type
    if (content instanceof ArrayBuffer || content instanceof Uint8Array) {
      const uint8Array = content instanceof ArrayBuffer ? new Uint8Array(content) : content;
      this.python.FS.writeFile(filePath, uint8Array);
      size = uint8Array.byteLength;
    } else if (content instanceof Blob) {
      const arrayBuffer = await content.arrayBuffer();
      const uint8Array = new Uint8Array(arrayBuffer);
      this.python.FS.writeFile(filePath, uint8Array);
      size = uint8Array.byteLength;
    } else if (typeof content === 'string') {
      this.python.FS.writeFile(filePath, content);
      size = content.length;
    } else if (content === null || content === undefined) {
      throw new Error('Empty content provided');
    } else {
      // Try to stringify other types
      const stringContent = JSON.stringify(content);
      this.python.FS.writeFile(filePath, stringContent);
      size = stringContent.length;
    }

    // STEP 5: Update Journal with new metadata
    if (this.journal) {
      this.journal.updateFileMetadata(filePath, {
        hash: newHash,
        size: size,
        timestamp: Date.now()
      });
    }

    // STEP 6: Persist to LocalforageService
    if (this.localforageService) {
      try {
        await this.localforageService.setItem(`${PYFS_CACHE_KEYS.META_PREFIX}${filePath}`, {
          hash: newHash,
          size: size,
          timestamp: Date.now()
        });
      } catch (error) {
        console.debug('Python filesystem: failed to cache file metadata', { filePath, error });
      }
    }

    return size;
  }

  /**
   * Fetch file content from services
   */
  private async fetchFileContent(file: NotebookFile, services: PyFsFileServices): Promise<FileContent> {
    if (!services?.fileUnifiedService || !services?.fileDataService) {
      throw new Error('Required services not provided for file fetching');
    }

    const responseType = this.getFileResponseType(file.name, services.fileDataService);

    return await services.fileUnifiedService.getFile(
      [file.name, responseType],
      this.chunkContext
    );
  }

  /**
   * Verify file accessibility from Python
   */
  private async verifyFileAccess(fileName: string): Promise<boolean> {
    try {
      const fullPath = `${this.config.workingDir}/${fileName}`;
      const pythonCode = `
        import os
        import json
        exists_relative = os.path.exists('${fileName}')
        exists_absolute = os.path.exists('${fullPath}')
        print(f"File ${fileName}: relative={exists_relative}, absolute={exists_absolute}, cwd={os.getcwd()}")
        json.dumps(exists_relative or exists_absolute)
      `;
      return this.executePythonCode<boolean>(pythonCode) === true;
    } catch (error) {
      console.warn('Python filesystem: verify file access failed', { fileName, error });
      return false;
    }
  }

  /**
   * Verify multiple files accessibility
   */
  async verifyFilesAccessibility(fileNames: string[]): Promise<{ success: boolean, errors: string[] }> {
    const errors: string[] = [];
    const inaccessibleFiles: string[] = [];

    if (fileNames.length === 0) {
      return { success: true, errors };
    }

    // Reuse the corrected logic from verifyFileAccess (no code duplication)
    for (const fileName of fileNames) {
      const accessible = await this.verifyFileAccess(fileName);  // Uses config.workingDir
      if (!accessible) {
        inaccessibleFiles.push(fileName);
      }
    }

    if (inaccessibleFiles.length > 0) {
      errors.push(`Files not accessible: ${inaccessibleFiles.join(', ')}`);
    }

    return { success: inaccessibleFiles.length === 0, errors };
  }

  /**
   * Unmount files from Pyodide filesystem
   */
  async unmountFiles(filesToUnmount?: string[]): Promise<UnmountResult> {
    const startTime = performance.now();
    const result: UnmountResult = {
      removedFiles: 0,
      removedDirectories: 0,
      errors: [],
      duration: 0,
      success: true  // Add success field for test compatibility
    };

    if (!this.python) {
      result.errors.push('Python runtime not available');
      result.success = false;  // Set to false on error
      return result;
    }


    try {
      const filesToRemove = filesToUnmount || this.getMountedFilePaths();

      for (const filePath of filesToRemove) {
        try {
          await this.removeSinglePath(filePath, result);
        } catch (error) {
          result.errors.push(`Failed to remove ${filePath}: ${GeneralHelpers.errorMessage(error)}`);
        }
      }

      result.duration = performance.now() - startTime;
      result.success = result.errors.length === 0;

    } catch (error) {
      result.errors.push(`Unmounting process failed: ${GeneralHelpers.errorMessage(error)}`);
      result.success = false;  // Set to false on catch
    }

    // Set success based on whether errors occurred
    result.success = result.errors.length === 0;

    return result;
  }

  /**
   * Remove a single path (file or directory)
   */
  private async removeSinglePath(filePath: string, result: UnmountResult): Promise<void> {
    // Skip system directories
    if (this.isSystemPath(filePath)) {
      return;
    }

    // Skip if path doesn't exist
    if (!this.python.FS.analyzePath(filePath).exists) {
      return;
    }

    const stats = this.python.FS.stat(filePath);

    if (this.python.FS.isFile(stats.mode)) {
      this.python.FS.unlink(filePath);
      result.removedFiles++;
    } else if (this.python.FS.isDir(stats.mode)) {
      // Only remove if empty or within working directory
      if (filePath.startsWith(this.config.workingDir + '/')) {
        try {
          this.python.FS.rmdir(filePath);
          result.removedDirectories++;
        } catch (error) {
          console.debug('Python filesystem: directory not empty on remove', { filePath, error });
        }
      }
    }
  }

  /**
   * Check if path is a system path that shouldn't be modified
   */
  private isSystemPath(path: string): boolean {
    return PythonFileSystemHelper.isSystemPath(path, this.config.workingDir);
  }

  /**
   * Static method to check if path is a system path that shouldn't be modified
   * Updated: Filesystem Restructure - removed mountDir parameter
   */
  static isSystemPath(path: string, workingDir?: string): boolean {
    const systemPaths = ['/', '/home', '/lib', '/usr', '/bin', '/sbin'];
    return systemPaths.includes(path) || (workingDir ? path === workingDir : false);
  }

  /**
   * Get paths of all mounted files
   */
  getMountedFilePaths(): string[] {
    const filePaths: string[] = [];

    try {
      this.iterateFileSystem('/', (path, stats) => {
        if (this.python.FS.isFile(stats.mode)) {
          filePaths.push(path);
        }
      });
    } catch (error) {
      console.warn('Python filesystem: error iterating filesystem', error);
    }

    return filePaths;
  }

  /**
   * Iterate through filesystem
   */
  private iterateFileSystem(startPath: string, callback: (path: string, stats: any) => void): void {
    const explore = (path: string) => {
      try {
        const contents = this.python.FS.readdir(path);
        contents.forEach((item: string) => {
          if (item === '.' || item === '..') return;

          const fullPath = `${path}/${item}`;
          try {
            const stats = this.python.FS.stat(fullPath);
            callback(fullPath, stats);

            if (this.python.FS.isDir(stats.mode)) {
              explore(fullPath);
            }
          } catch (error) {
            console.debug('Python filesystem: skip inaccessible path', { fullPath, error });
          }
        });
      } catch (error) {
        console.debug('Python filesystem: skip inaccessible directory', { path, error });
      }
    };

    explore(startPath);
  }

  /**
   * Get filesystem diagnostics
   */
  async diagnoseFileSystem(): Promise<FileSystemDiagnostics> {
    const diagnostics: FileSystemDiagnostics = {
      workingDirectory: '',
      mountedFilesCount: 0,
      availableFiles: [],
      totalSize: 0,
      errors: []
    };

    if (!this.python) {
      diagnostics.errors.push('Python runtime not available');
      return diagnostics;
    }

    try {
      const pythonCode = `
        import os
        import json

        result = {
          'cwd': os.getcwd(),
          'cwd_files': [],
          'working_dir_exists': os.path.exists('${this.config.workingDir}'),
          'files_subdir_exists': os.path.exists('./files'),
          'default_subdir_exists': os.path.exists('./default'),
          'project_subdir_exists': os.path.exists('./project')
        }

        # List files in current directory and all subdirectories
        try:
          files = []

          ${this.getPythonCodeForListingAllFiles()}

          result['cwd_files'] = files
        except Exception as e:
          result['cwd_error'] = str(e)

        json.dumps(result)
      `;

      const data = this.executePythonCode<{ cwd: string; cwd_files?: string[] }>(pythonCode);
      diagnostics.workingDirectory = data.cwd;
      diagnostics.availableFiles = data.cwd_files || [];
      diagnostics.mountedFilesCount = diagnostics.availableFiles.length;

    } catch (error) {
      diagnostics.errors.push(`Diagnostics failed: ${GeneralHelpers.errorMessage(error)}`);
    }

    return diagnostics;
  }

  /**
   * Phase 2: Journal-based unmounting of missing files (replaces PythonFileSystemTracker)
   * Uses journal metadata to decide which files should be preserved
   */
  private async unmountMissingFilesUsingJournal(newFiles: NotebookFile[]): Promise<number> {
    if (!this.journal) {
      return 0;
    }

    try {
      // Get currently mounted files
      const currentFiles = this.getCurrentlyMountedFiles();

      // Get new file names
      const newFileNames = new Set(newFiles.map(f => f.name));

      // Find files to remove (exist in current but not in new)
      const filesToRemove = currentFiles.filter(fileName => {
        // If file is in new list, keep it
        if (newFileNames.has(fileName)) {
          return false;
        }

        // Normalize path for consistent journal lookup
        const normalizedPath = PythonUtils.normalizePath(fileName, this.config.workingDir);

        // Fail-safe: if no metadata in journal, don't remove
        // This protects files when journal.clear() was called
        if (!this.journal!.hasMetadata(normalizedPath)) {
          return false;  // Don't remove files without metadata
        }

        // Has metadata - check persistent flag
        const shouldKeep = this.journal!.shouldKeepFile(normalizedPath);

        return !shouldKeep; // Remove if journal says don't keep
      });

      // Remove each missing file
      let removedCount = 0;
      for (const fileName of filesToRemove) {
        try {
          // Check if file exists using FS API (faster than python.runPython)
          if (this.python.FS.analyzePath(fileName).exists) {
            this.python.FS.unlink(fileName);
            removedCount++;
          }
        } catch (error) {
          console.warn('Python filesystem: failed to remove missing file', { fileName, error });
        }
      }

      return removedCount;
    } catch (error) {
      console.warn('Python filesystem: error removing missing files', error);
      return 0;
    }
  }

  /**
   * Get all currently mounted files in working directory
   */
  /**
   * Get all currently mounted files in working directory and subdirectories
   * Updated: Filesystem Restructure - lists files from all subdirectories
   */
  private getCurrentlyMountedFiles(): string[] {
    try {
      const pythonCode = `
import os
import json
files = []

${this.getPythonCodeForListingAllFiles()}

json.dumps(files)
      `;
      return this.executePythonCode<string[]>(pythonCode);
    } catch (error) {
      console.warn('Python filesystem: failed to list mounted files', error);
      return [];
    }
  }

  /**
   * Generate Python code to list all files from root and subdirectories
   * Reusable template for diagnostics and file listing
   * Reduces code duplication (Sonar fix)
   */
  private getPythonCodeForListingAllFiles(): string {
    return `
# Root directory files (temporary files not in subdirectories)
for item in os.listdir('.'):
    if os.path.isfile(item):
        files.append(item)

# Subdirectories (files/, default/, project/) - using constants
for subdir in ['${PYTHON_DIRECTORIES.FILES_SUBDIR}', '${PYTHON_DIRECTORIES.DEFAULT_SUBDIR}', '${PYTHON_DIRECTORIES.PROJECT_SUBDIR}']:
    subdir_path = f'./{subdir}'
    if os.path.exists(subdir_path):
        for item in os.listdir(subdir_path):
            if os.path.isfile(os.path.join(subdir_path, item)):
                files.append(item)
    `.trim();
  }

  /**
   * Update configuration
   */
  updateConfig(newConfig: Partial<FileSystemConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * PHASE 4: Get all files from all sources (unified approach)
   * Combines notebook files and module list files to avoid duplication
   * Loads content for files that don't have it
   */
  async getAllFilesFromAllSources(
    fileService: FileService,
    notebookFilesService: NotebookFilesService,
    fileDataService: FileDataService
  ): Promise<NotebookFile[]> {
    try {
      // SOURCE 1: Notebook files (project files)
      const notebookFiles = await notebookFilesService.getNotebookFiles();

      // SCRUM-166: Parallelize content fetching instead of sequential for...of loop
      const contentPromises = notebookFiles
        .filter(file => !(file as PyFsFile).content && file.fileId)
        .map(async (file) => {
          try {
            const responseType = this.getFileResponseType(file.name, fileDataService);
            const content = await notebookFilesService.getFileContent(
              String(file.fileId),
              file.isDefaultFile || false,
              responseType
            );
            if (content) {
              (file as PyFsFile).content = content;
            }
          } catch (error) {
            console.warn('Python filesystem: failed to load file content', { fileName: file.name, error });
          }
        });

      await Promise.all(contentPromises);

      // SOURCE 2: Module List files (global modules)
      const moduleListPy = await fileService.getFileModuleList([FILE_EXTENSIONS.py]);
      // TODO: Revise this thing because whl should be loaded firstly after other things
      const moduleListWhl = await fileService.getFileModuleList([FILE_EXTENSIONS.whl]);

      // Convert module list to NotebookFile format
      const pyModules = await this.processModuleListItems(moduleListPy, fileService, 'python', 'text');
      const whlModules = await this.processModuleListItems(moduleListWhl, fileService, 'wheel', 'arraybuffer');
      const convertedModules = [...pyModules, ...whlModules];

      // DEDUPLICATE: Merge files preserving Module List metadata (versionId, source)
      // Strategy: Module List metadata takes priority for routing to ./project/
      const moduleListMap = new Map(
        convertedModules.map(m => [m.name, m])
      );

      // Merge with priority to Module List metadata for routing
      const allFiles = notebookFiles.map((f: NotebookFile) => {
        const moduleVersion = moduleListMap.get(f.name);

        if (moduleVersion) {
          // File exists in BOTH sources - preserve Module List metadata for routing
          moduleListMap.delete(f.name);  // Mark as processed

          return {
            ...f,                                    // Keep NotebookFile data (cacheId, isDefaultFile, etc.)
            versionId: moduleVersion.versionId,      // ✅ Preserve versionId from Module List
            source: 'moduleList',                    // ✅ Preserve source for routing
            isGlobal: (moduleVersion as PyFsFile).isGlobal,
            content: (f as PyFsFile).content || (moduleVersion as PyFsFile).content  // Prefer NotebookFile content
          } as PyFsFile;
        }

        // File only in Notebook Files - keep as is
        return {
          ...f,
          source: 'notebook',
          isGlobal: false
        } as PyFsFile;
      });

      // Add remaining Module List files not in Notebook Files
      allFiles.push(...Array.from(moduleListMap.values()));

      return allFiles;

    } catch (error) {
      console.warn('Python filesystem: failed to load all sources, falling back', error);
      // Fallback to just notebook files
      try {
        return await notebookFilesService.getNotebookFiles();
      } catch (error) {
        console.warn('Python filesystem: fallback to notebook files failed', error);
        return [];
      }
    }
  }

  /**
   * PHASE 5: Install package only (for sequential loading)
   * Only writes to FS and installs package - no other processing
   * Updated: Supports subdirectory routing (.whl typically go to root)
   */
  async installPackageOnly(
    fileName: string,
    content: FileContent,
    _metadata: {
      origin: 'user' | 'project' | 'generated' | 'cache';
      purpose?: 'module' | 'data' | 'output' | 'temp';
      persistent?: boolean;
    },
    file?: NotebookFile & { source?: string; isGlobal?: boolean }  // Optional: for routing
  ): Promise<{ success: boolean; path: string; errors?: string[] }> {
    const filePath = this.buildFilePath(fileName, file);

    const result = await this.executeFileOperation(async () => {
      // Write to FS
      // FileSystemProxy will automatically journal this operation
      await this.writeFileToFS(filePath, content);

      // Install package if .whl
      await this.processFileByExtension(fileName, filePath);
    }, fileName, filePath, 'Failed to install package');

    return result;
  }

  /**
   * PHASE 5: Load module only (for sequential loading)
   * Only writes to FS and registers Python file - no package installation
   * Updated: Filesystem Restructure - supports subdirectories (files/default/project)
   */
  async loadModuleOnly(
    fileName: string,
    content: FileContent,
    _metadata: {
      origin: 'user' | 'project' | 'generated' | 'cache';
      purpose?: 'module' | 'data' | 'output' | 'temp';
      persistent?: boolean;
    },
    file?: NotebookFile & { source?: string }  // Optional: file metadata for subdirectory routing
  ): Promise<{ success: boolean; path: string; errors?: string[] }> {
    return this.loadFileWithTrustedOperation(
      fileName,
      content,
      'module',
      file,
      `Failed to load module ${fileName}`
    );
  }

  /**
   * PHASE 5: Load data file only (for sequential loading)
   * Only writes to FS - no special processing
   * Updated: Filesystem Restructure - supports subdirectories (files/default/project)
   */
  async loadDataFileOnly(
    fileName: string,
    content: FileContent,
    _metadata: {
      origin: 'user' | 'project' | 'generated' | 'cache';
      purpose?: 'module' | 'data' | 'output' | 'temp';
      persistent?: boolean;
    },
    file?: NotebookFile & { source?: string }  // Optional: file metadata for subdirectory routing
  ): Promise<{ success: boolean; path: string; errors?: string[] }> {
    return this.loadFileWithTrustedOperation(
      fileName,
      content,
      'data',
      file,
      `Failed to load data file ${fileName}`
    );
  }

  /**
   * Update chunk context
   */
  updateChunkContext(context: ExecutionContext): void {
    this.chunkContext = context;
  }

  // EHQ-104: syncCacheFiles() / syncDefaultFiles() run only for files touched
  // during the current Python run (see getMountedFiles run-boundary filter).
  // Unchanged mounted backend files are no longer re-hashed/re-synced every run.
  // Changed files are batch-upserted in one request per file class.
  /**
   * Sync files from Pyodide FS back to backend
   * Routes files by directory to appropriate API:
   * - ./files/ + ROOT → Cache API
   * - ./default/ → Default Files API
   * - ./project/ → Skip (READ-ONLY)
   *
   * @param files - Map of fullPath → timestamp (from getMountedFiles)
   */
  async syncFilesToBackend(
    files: Map<string, string>,
    services: {
      python: any;
      cacheService: CacheService;
      notebookDefaultFilesService: NotebookDefaultFilesService;
      appStateService: AppStateService;
      messageService: MessageService;
      notebookFiles?: NotebookFile[];
    },
    workingDir: string
  ): Promise<void> {
    if (files.size === 0) {
      return;
    }

    // Separate files by directory (Map remains fullPath → timestamp)
    const cacheFiles = new Map<string, string>();
    const defaultFiles = new Map<string, string>();
    let _projectFilesSkipped = 0;

    const filesDir = `${workingDir}/${PYTHON_DIRECTORIES.FILES_SUBDIR}/`;
    const defaultDir = `${workingDir}/${PYTHON_DIRECTORIES.DEFAULT_SUBDIR}/`;
    const projectDir = `${workingDir}/${PYTHON_DIRECTORIES.PROJECT_SUBDIR}/`;

    for (const [filePath, timestamp] of files) {
      if (filePath.startsWith(filesDir)) {
        cacheFiles.set(filePath, timestamp);
      } else if (filePath.startsWith(defaultDir)) {
        defaultFiles.set(filePath, timestamp);
      } else if (filePath.startsWith(projectDir)) {
        _projectFilesSkipped++;
      } else {
        // ROOT files → Cache (backward compatibility)
        cacheFiles.set(filePath, timestamp);
      }
    }

    // Sync to appropriate backends
    if (cacheFiles.size > 0) {
      await this.syncCacheFiles(cacheFiles, services);
    }

    if (defaultFiles.size > 0) {
      await this.syncDefaultFiles(defaultFiles, services);
    }
  }

  /**
   * Common file sync logic
   * Reduces code duplication (Sonar fix)
   *
   * EHQ-104: After local hash filtering, remaining files are uploaded via
   * batch upsert when `batchUpsert` is provided, chunked to the server's
   * part-count (100) and aggregate-byte (64 MiB) caps. Falls back to sequential
   * create/update only when the batch *route* is absent (404 with no `results`
   * body). A 404 that carries `results` is a real batch outcome (foreign
   * cache / all-item NOT_FOUND), not a missing endpoint. Partial failures
   * (including 207 MULTI_STATUS, which Angular delivers as success) are
   * tracked and surfaced.
   *
   * @param files - Map of fullPath → timestamp
   * @param python - Python runtime instance
   * @param fileType - File class for update payload selection
   * @param getExistingFiles - Function to get existing files
   * @param getExistingContent - Function to get existing file content
   * @param batchUpsert - Optional batch upsert of changed files (preferred)
   * @param updateFile - Fallback: update a single file
   * @param createFile - Fallback: create a single file
   * @returns Sync statistics including failed count
   */
  private async syncFilesCommon(
    files: Map<string, string>,
    python: any,
    fileType: 'cache' | 'default',
    getExistingFiles: () => Promise<SyncFileRecord[]>,
    getExistingContent: (file: SyncFileRecord, fileName: string) => Promise<ArrayBuffer | null>,
    batchUpsert: ((payload: FormData) => Promise<BatchUpsertResponse>) | null,
    updateFile: (file: SyncFileRecord, fileName: string, payload: FormData | Blob) => Promise<void>,
    createFile: (fileName: string, formData: FormData) => Promise<void>
  ): Promise<{ filesCreated: number; filesUpdated: number; filesSkipped: number; filesFailed: number }> {
    const existingFiles = await getExistingFiles();
    let filesCreated = 0;
    let filesUpdated = 0;
    let filesSkipped = 0;
    let filesFailed = 0;

    // Build a Map for O(1) existing file lookup instead of O(n) per file.
    //
    // Keyed by basename because that is all there is: `SyncFileRecord` carries
    // no path — the backend returns `name` only. So a repeated basename is
    // genuinely unresolvable here, and letting the last writer win would turn a
    // second file into an "update" of the first and lose it silently. Collect
    // the collisions instead and refuse to sync exactly those files.
    const existingFilesMap = new Map<string, SyncFileRecord>();
    const ambiguousNames = new Set<string>();
    for (const file of existingFiles) {
      if (!file.name) continue;
      if (existingFilesMap.has(file.name)) {
        ambiguousNames.add(file.name);
        continue;
      }
      existingFilesMap.set(file.name, file);
    }
    if (ambiguousNames.size > 0) {
      console.error(
        'Python filesystem: duplicate basenames among existing files — these will be skipped rather than risk overwriting the wrong file',
        Array.from(ambiguousNames)
      );
    }

    // The same collision can arise entirely on the local side. The FS walk
    // recurses (see iterateFileSystem), so `files/report.xlsx` and
    // `files/sub/report.xlsx` are two entries that flatten to one backend name.
    // Both would be appended to the batch under that single part name, results
    // correlate by name, and cacheSyncedBatchHashes would then record a synced
    // hash for the path that never landed — so the next run skips it and the
    // file is silently lost. Refuse both, as with a backend collision.
    const localNameCounts = new Map<string, number>();
    for (const filePath of files.keys()) {
      const name = GeneralHelpers.getNameWithExtension(filePath);
      localNameCounts.set(name, (localNameCounts.get(name) ?? 0) + 1);
    }
    const localDuplicateNames = Array.from(localNameCounts)
      .filter(([, count]) => count > 1)
      .map(([name]) => name);
    if (localDuplicateNames.length > 0) {
      console.error(
        'Python filesystem: two or more files in this run share a basename — the backend keys by name only, so these will be skipped rather than overwrite each other',
        localDuplicateNames
      );
      for (const name of localDuplicateNames) ambiguousNames.add(name);
    }

    type PendingUpload = {
      filePath: string;
      fileName: string;
      timestamp: string;
      bytes: Uint8Array;
      blob: Blob;
      newHash: string;
      existingFile?: SyncFileRecord;
    };
    const pendingUploads: PendingUpload[] = [];

    for (const [filePath, timestamp] of files) {
      const fileName = GeneralHelpers.getNameWithExtension(filePath);
      if (ambiguousNames.has(fileName)) {
        // Another record — on the backend or in this same run — carries this
        // basename, and the backend addresses files by name alone. We cannot
        // tell which one an update would hit, so failing this file is the only
        // non-destructive option.
        console.error(
          'Python filesystem: refusing to sync a file whose basename is ambiguous',
          { filePath, fileName }
        );
        filesFailed++;
        continue;
      }
      const existingFile = existingFilesMap.get(fileName);

      try {
        // Read file from Pyodide FS
        const fileContent = python.FS.readFile(filePath, { encoding: 'binary' });
        const newHash = await GeneralHelpers.calculateFileHash(fileContent);

        // Check journal/localforage for cached backend hash to avoid API call
        if (existingFile?.fileId) {
          let existingHash: string | null = null;

          // Try cached hash from localforage first (avoids expensive API roundtrip)
          if (this.localforageService) {
            try {
              const cachedMeta = await this.localforageService.getItem(`${PYFS_CACHE_KEYS.SYNCED_PREFIX}${filePath}`) as { hash?: string } | null;
              if (cachedMeta?.hash) {
                existingHash = cachedMeta.hash;
              }
            } catch {
              // Cache miss - fall through to API
            }
          }

          // Fallback: fetch existing content from backend for hash comparison
          if (!existingHash) {
            const existingContent = await getExistingContent(existingFile, fileName);
            if (existingContent) {
              existingHash = await GeneralHelpers.calculateFileHash(existingContent);
            }
          }

          if (existingHash && newHash === existingHash) {
            filesSkipped++;
            continue;
          }
        }

        // Narrow explicitly rather than String(fileContent): Pyodide's
        // FS.readFile returns Uint8Array for { encoding: 'binary' } and string
        // for 'utf8', but if it ever handed back an ArrayBuffer the String()
        // cast would yield "[object ArrayBuffer]" and upload 21 bytes of
        // garbage in place of the file. Refuse the file instead.
        let bytes: Uint8Array;
        if (fileContent instanceof Uint8Array) {
          bytes = fileContent;
        } else if (typeof fileContent === 'string') {
          bytes = new TextEncoder().encode(fileContent);
        } else {
          throw new Error(
            `Unsupported content type from FS.readFile: ${Object.prototype.toString.call(fileContent)}`
          );
        }
        const blob = new Blob([bytes as BlobPart], { type: 'application/octet-stream' });

        pendingUploads.push({
          filePath,
          fileName,
          timestamp,
          bytes,
          blob,
          newHash,
          existingFile,
        });
      } catch (error) {
        // Count it: a file dropped here never reaches pendingUploads, so
        // without this it vanishes from the totals and the run looks clean.
        filesFailed++;
        console.warn('Python filesystem: failed to prepare file for sync', { fileName, error });
      }
    }

    if (pendingUploads.length === 0) {
      return { filesCreated, filesUpdated, filesSkipped, filesFailed };
    }

    const sequentialRetry: PendingUpload[] = [];
    if (batchUpsert) {
      // A file over the aggregate batch cap can never ride in a batch, however
      // it is chunked — the server rejects the whole request on total bytes,
      // and that rejection carries no `results`, so it lands in the branch that
      // counts the chunk failed with no retry. The single-file endpoint has its
      // own, larger limit (ehq-server `max_file_size`, 100 MiB by default,
      // against a 64 MiB `max_batch_size`), so such a file used to sync fine one
      // at a time. Send it there directly instead of spending a doomed request.
      const batchable: PendingUpload[] = [];
      for (const pending of pendingUploads) {
        if (pending.blob.size > MAX_BATCH_BYTES) {
          sequentialRetry.push(pending);
        } else {
          batchable.push(pending);
        }
      }

      const chunks = chunkSyncUploads(batchable);
      let endpointAbsent = false;

      for (const chunk of chunks) {
        if (endpointAbsent) {
          sequentialRetry.push(...chunk);
          continue;
        }

        const sentNames = chunk.map((pending) => pending.fileName);
        try {
          // Do not use GeneralHelpers.prepareFileFormData here: it appends a
          // `name` field, and the server rejects any multipart part whose field
          // name is not `file` with 400 for the whole request.
          const batchForm = new FormData();
          for (const pending of chunk) {
            batchForm.append('file', pending.blob, pending.fileName);
          }

          // 207 MULTI_STATUS is the documented mixed-success path; Angular
          // treats any 2xx as success so the body lands here.
          const response = await batchUpsert(batchForm);
          const parsed = this.parseBatchUpsertResults(response, sentNames);
          filesCreated += parsed.created.length;
          filesUpdated += parsed.updated.length;
          await this.cacheSyncedBatchHashes(chunk, parsed);
          this.logBatchItemFailures(fileType, parsed);

          if (parsed.failed.size > 0) {
            sequentialRetry.push(
              ...chunk.filter((pending) => parsed.failed.has(pending.fileName))
            );
          }
        } catch (batchError: unknown) {
          const errorBody = batchResponseBody(batchError);
          if (errorBody) {
            const parsed = this.parseBatchUpsertResults(errorBody, sentNames);
            filesCreated += parsed.created.length;
            filesUpdated += parsed.updated.length;
            await this.cacheSyncedBatchHashes(chunk, parsed);
            this.logBatchItemFailures(fileType, parsed);
            filesFailed += parsed.failed.size;
            continue;
          }

          if (isBatchEndpointAbsent(batchError)) {
            console.warn(
              'Python filesystem: batch upsert endpoint unavailable, falling back to sequential',
              batchError
            );
            endpointAbsent = true;
            sequentialRetry.push(...chunk);
            continue;
          }

          const errRecord = batchError as { status?: number; message?: string };
          const isContextError =
            !!errRecord?.message?.includes('Missing') &&
            !!errRecord?.message?.includes('context');
          if (isContextError) {
            console.warn('Python filesystem: batch upsert aborted due to missing context', batchError);
          } else {
            console.warn('Python filesystem: batch upsert rejected (no sequential fallback)', {
              status: errRecord?.status,
              message: errRecord?.message,
              error: batchError,
            });
          }
          filesFailed += chunk.length;
        }
      }
    }

    const toUploadSequentially = batchUpsert ? sequentialRetry : pendingUploads;
    if (toUploadSequentially.length > 0) {
      for (const pending of toUploadSequentially) {
        try {
          const formData = GeneralHelpers.prepareFileFormData(
            pending.fileName,
            pending.bytes,
            Number(pending.timestamp)
          );
          const blob = pending.blob;

          // SyncFileRecord declares both `fileId` and `id`, so no cast is needed.
          // Kept `||` rather than `??` on purpose: a falsy fileId (0 or "") should
          // fall through to `id`, which is the behaviour the `if (fileId)` below
          // already assumes.
          const fileId = pending.existingFile?.fileId || pending.existingFile?.id;
          if (fileId) {
            const updatePayload = fileType === 'default' ? blob : formData;
            await updateFile(pending.existingFile!, pending.fileName, updatePayload);
            filesUpdated++;
          } else {
            try {
              await createFile(pending.fileName, formData);
              filesCreated++;
            } catch (createError) {
              if (this.isDuplicateFileError(createError)) {
                filesSkipped++;
              } else {
                throw createError;
              }
            }
          }

          if (this.localforageService) {
            try {
              await this.localforageService.setItem(`${PYFS_CACHE_KEYS.SYNCED_PREFIX}${pending.filePath}`, {
                hash: pending.newHash,
                timestamp: Date.now()
              });
            } catch {
              // Non-critical
            }
          }
        } catch (error) {
          console.warn(`Python filesystem: failed to sync ${fileType} file`, { fileName: pending.fileName, error });
          filesFailed++;
        }
      }
    }

    return { filesCreated, filesUpdated, filesSkipped, filesFailed };
  }

  private async cacheSyncedBatchHashes(
    chunk: Array<{ fileName: string; filePath: string; newHash: string }>,
    parsed: { created: string[]; updated: string[] }
  ): Promise<void> {
    if (!this.localforageService) return;
    const syncedNames = new Set([...parsed.created, ...parsed.updated]);
    for (const pending of chunk) {
      if (!syncedNames.has(pending.fileName)) continue;
      try {
        await this.localforageService.setItem(`${PYFS_CACHE_KEYS.SYNCED_PREFIX}${pending.filePath}`, {
          hash: pending.newHash,
          timestamp: Date.now(),
        });
      } catch {
        // Non-critical
      }
    }
  }

  private logBatchItemFailures(
    fileType: 'cache' | 'default',
    parsed: { failed: Map<string, BatchUpsertError> }
  ): void {
    for (const [name, err] of parsed.failed) {
      console.warn('Python filesystem: batch item failed', {
        fileType,
        fileName: name,
        code: err.code,
        message: err.message,
      });
    }
  }

  /**
   * Helper to parse batch upsert API response, correlating against sent names
   * to detect sent-but-missing files (EHQ-104).
   */
  private parseBatchUpsertResults(
    response: BatchUpsertResponse,
    sentNames: string[]
  ): { created: string[]; updated: string[]; failed: Map<string, BatchUpsertError> } {
    const created: string[] = [];
    const updated: string[] = [];
    const failed = new Map<string, BatchUpsertError>();
    const reported = new Set<string>();
    for (const item of response?.results ?? []) {
      reported.add(item.name);
      if (item.action === 'created') created.push(item.name);
      else if (item.action === 'updated') updated.push(item.name);
      else if (item.action === 'error') {
        failed.set(
          item.name,
          item.error ?? { code: 'UNKNOWN', message: 'Unknown batch error' }
        );
      }
    }
    // Correlate against what we sent: a name absent from results is an
    // unreported failure (e.g. silently dropped empty part on an old server).
    for (const name of sentNames) {
      if (!reported.has(name) && !failed.has(name)) {
        failed.set(name, {
          code: 'MISSING_FROM_BATCH_RESULTS',
          message: 'File was sent in the batch but not reported in results',
        });
      }
    }
    return { created, updated, failed };
  }

  /**
   * Sync files to Cache API
   * Checks file hash to skip unchanged files
   */
  private async syncCacheFiles(
    files: Map<string, string>,
    services: {
      python: any;
      cacheService: CacheService;
      messageService: MessageService;
      notebookFiles?: NotebookFile[];
    }
  ): Promise<void> {
    // Get current cache files
    const getExistingFiles = async (): Promise<SyncFileRecord[]> => {
      // Primary: Use notebookFiles if available (fresh from syncProjectFiles)
      if (services.notebookFiles && services.notebookFiles.length > 0) {
        return services.notebookFiles;
      }

      // Fallback: Try getCacheContent() (may be cleared during save)
      try {
        const cacheFiles = await firstValueFrom(services.cacheService.getCacheContent());
        if (cacheFiles && cacheFiles.length > 0) {
          return cacheFiles;
        }
      } catch (error) {
        console.warn('Python filesystem: error getting cache content', error);
      }

      return [];
    };

    const getExistingContent = async (file: SyncFileRecord, fileName: string): Promise<ArrayBuffer | null> => {
      return file.content || await this.getFileContentFromBackend(String(file.fileId), fileName, services);
    };

    const updateFile = async (file: SyncFileRecord, _fileName: string, payload: FormData | Blob): Promise<void> => {
      await firstValueFrom(services.cacheService.updateCacheContentFile(String(file.fileId ?? file.id), payload as FormData));
    };

    const createFile = async (fileName: string, formData: FormData): Promise<void> => {
      await firstValueFrom(services.cacheService.createCacheContent(formData));
    };

    const batchUpsert = async (formData: FormData) => {
      return await firstValueFrom(services.cacheService.batchUpsertCacheContent(formData));
    };

    const stats = await this.syncFilesCommon(
      files,
      services.python,
      'cache',
      getExistingFiles,
      getExistingContent,
      batchUpsert,
      updateFile,
      createFile
    );

    this.notifyUIAfterFileSync(stats, services.messageService);
  }

  /**
   * Sync files to Default Files API
   * Checks file hash to skip unchanged files
   */
  private async syncDefaultFiles(
    files: Map<string, string>,
    services: {
      python: any;
      notebookDefaultFilesService: NotebookDefaultFilesService;
      appStateService: AppStateService;
      messageService: MessageService;
    }
  ): Promise<void> {
    const projectId = services.appStateService.projectId();
    const workflowId = services.appStateService.workflowId();

    if (!projectId || !workflowId) {
      return;
    }

    const getExistingFiles = async (): Promise<SyncFileRecord[]> => {
      try {
        return await firstValueFrom(
          services.notebookDefaultFilesService.getDefaultFiles(projectId, workflowId)
        );
      } catch (error) {
        console.warn('Python filesystem: failed to load default files', error);
        return [];
      }
    };

    const getExistingContent = async (file: SyncFileRecord, _fileName: string): Promise<ArrayBuffer | null> => {
      return await firstValueFrom(
        services.notebookDefaultFilesService.getDefaultFileContents(
          projectId,
          workflowId,
          file.fileId != null ? String(file.fileId) : null,
          'arraybuffer'
        )
      ).catch(() => null);
    };

    const updateFile = async (file: SyncFileRecord, _fileName: string, payload: FormData | Blob): Promise<void> => {
      await firstValueFrom(
        services.notebookDefaultFilesService.updateDefaultFileContents(
          projectId,
          workflowId,
          file.fileId != null ? String(file.fileId) : null,
          payload as Blob
        )
      );
    };

    const createFile = async (fileName: string, formData: FormData): Promise<void> => {
      await firstValueFrom(
        services.notebookDefaultFilesService.createDefaultFile(projectId, workflowId, formData)
      );
    };

    const batchUpsert = async (formData: FormData) => {
      return await firstValueFrom(
        services.notebookDefaultFilesService.batchUpsertDefaultFiles(projectId, workflowId, formData)
      );
    };

    const stats = await this.syncFilesCommon(
      files,
      services.python,
      'default',
      getExistingFiles,
      getExistingContent,
      batchUpsert,
      updateFile,
      createFile
    );

    this.notifyUIAfterFileSync(stats, services.messageService);
  }

  /**
   * Get file content from backend with fallback logic
   * Tries Cache → NotebookFiles → FileService
   */
  private async getFileContentFromBackend(
    fileId: string,
    fileName: string,
    services: {
      cacheService: CacheService;
      notebookFilesService?: NotebookFilesService;
      fileService?: FileService;
      fileDataService?: FileDataService;
    }
  ): Promise<ArrayBuffer | null> {
    try {
      // Try cache first
      const cacheContent = await firstValueFrom(
        services.cacheService.getCacheContentFile(fileId, 'arraybuffer')
      ).catch(() => null);

      if (cacheContent) return cacheContent;

      // Try notebook files
      if (services.notebookFilesService) {
        const notebookContent = await services.notebookFilesService
          .getFileContent(fileId, true, 'arraybuffer')
          .catch(() => null);

        if (notebookContent) return notebookContent;
      }

      // Try file service
      if (services.fileService && services.fileDataService) {
        const responseType = this.getFileResponseType(fileName, services.fileDataService);
        const fileContent = await firstValueFrom(
          services.fileService.getById(fileId, responseType)
        ).catch(() => null);

        return fileContent;
      }

      return null;
    } catch (error) {
      console.warn('Python filesystem: failed to load file content from backend', { fileId, fileName, error });
      return null;
    }
  }

}
